/**
 * FIND — query grammar parser (spec docs/plans/search-redesign.md §5.3).
 *
 * Parses the raw palette/search string into a structured `ParsedQuery` of `{ text tokens, filters }`.
 * Pure and deterministic (a fixed `now` can be injected), so it unit-tests in plain Node and produces
 * identical results on every tier. The SAME object then drives FTS5 `MATCH` + SQL `WHERE` on the
 * server and both client tiers (see compile.ts) — parse once, run everywhere.
 *
 * Grammar (a small Obsidian subset + Bear `@` specials):
 *   term term          implicit AND         "exact phrase"     phrase
 *   -term              negation             a OR b, ( … )      boolean / grouping
 *   tag:#x  -tag:x     exact tag            title:foo          title+alias scope
 *   in:notes|journal|docs|email|signals|people|tasks   corpus scope (negatable: -in:x)
 *   before:/after:/on:2026-07   updated-date filters (created:/created-before:/created-after: too)
 *   link:[[Page]]      links-to-entity      @task @todo @done @image @file @today @last7days @starred
 *
 * Operators compile to EXACT SQL WHERE (tags/entities/corpus/date/starred). Only free text and
 * phrases reach FTS `MATCH`. Every recognized operator is echoed in `filters` so the UI can render a
 * removable chip; unrecognized operator-looking tokens are demoted to plain text with a warning.
 */
import { PageType, type NoteEntityType } from '../types.js';
import { isIsoDate } from '../dates/org.js';
import type { Corpus } from './schema.js';

/** Content-type / status specials (@task etc.) that aren't dates or starred. */
export const SPECIALS = ['task', 'todo', 'done', 'image', 'file'] as const;
export type Special = (typeof SPECIALS)[number];

/** A half-open epoch-ms range [gte, lt). Either bound may be absent (unbounded on that side). */
export interface DateRange {
  gte?: number;
  lt?: number;
}

/** A single free-text atom that reaches FTS `MATCH`. */
export type TextToken =
  | { kind: 'term'; value: string; negated: boolean; prefix: boolean; field: FieldScope | null }
  | { kind: 'phrase'; value: string; negated: boolean; field: FieldScope | null }
  | { kind: 'or' }
  | { kind: 'group-open' }
  | { kind: 'group-close' };

/** Column scope from a `field:` prefix. `title` scopes MATCH to the title+aliases columns. */
export type FieldScope = 'title';

/** Structured, exact-match filters extracted from operators — all AND-combined. */
export interface QueryFilters {
  /** `tag:#x` → exact membership in docs.tags (normalized, no leading #). */
  tags: string[];
  notTags: string[];
  /** `in:email` → docs.corpus membership. */
  corpora: Corpus[];
  notCorpora: Corpus[];
  /** `in:journal` / `in:docs` → docs.page_type (note-family only). */
  pageTypes: PageType[];
  notPageTypes: PageType[];
  /** FACET P8 — `is:person|company|deal|topic` → docs.entity_type (typed pages). */
  entityTypes: NoteEntityType[];
  notEntityTypes: NoteEntityType[];
  /** `link:[[Page]]` → target names, resolved to entity ids by the caller (graph, aliases included). */
  links: string[];
  /** `before:/after:/on:` bounds on docs.updated_at. */
  updated?: DateRange;
  /** `created:/created-before:/created-after:` bounds on docs.created_at. */
  created?: DateRange;
  /** `@task @todo @done @image @file` content/status specials. */
  specials: Special[];
  /** `@starred` — undefined = no constraint. */
  starred?: boolean;
}

export interface ParsedQuery {
  /** Free-text atoms + boolean structure for FTS `MATCH`. */
  tokens: TextToken[];
  filters: QueryFilters;
  /** Human-friendly residual text (positive terms/phrases) — feeds the KNN embedding and display. */
  text: string;
  /** Non-fatal parse notes for the "Explain search" hint line. */
  warnings: string[];
}

/** `in:` scope words → corpus and/or page_type. `notes` is the whole note family (both page types). */
const IN_ALIASES: Record<string, { corpus?: Corpus; pageType?: PageType }> = {
  notes: { corpus: 'note' },
  note: { corpus: 'note' },
  journal: { pageType: PageType.Journal },
  journals: { pageType: PageType.Journal },
  docs: { pageType: PageType.Document },
  doc: { pageType: PageType.Document },
  document: { pageType: PageType.Document },
  documents: { pageType: PageType.Document },
  email: { corpus: 'email' },
  emails: { corpus: 'email' },
  mail: { corpus: 'email' },
  signal: { corpus: 'signal' },
  signals: { corpus: 'signal' },
  people: { corpus: 'contact' },
  person: { corpus: 'contact' },
  contact: { corpus: 'contact' },
  contacts: { corpus: 'contact' },
  task: { corpus: 'task' },
  tasks: { corpus: 'task' },
  entity: { corpus: 'entity' },
  entities: { corpus: 'entity' },
};

function emptyFilters(): QueryFilters {
  return { tags: [], notTags: [], corpora: [], notCorpora: [], pageTypes: [], notPageTypes: [], entityTypes: [], notEntityTypes: [], links: [], specials: [] };
}

/**
 * Split raw input into tokens. A token is a run of non-space chars that may embed spaces INSIDE a
 * `"quoted phrase"` or a `[[link target]]` — so `link:[[Acme Corp]]` and `title:"a b"` stay whole.
 */
function lex(raw: string): string[] {
  const out: string[] = [];
  const re = /(?:[^\s"[\]]+|"[^"]*"?|\[\[[^\]]*\]\]?)+/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(raw)) !== null) if (m[0]) out.push(m[0]);
  return out;
}

/** Merge a new range into an existing one by intersection (both constraints must hold). */
function intersect(base: DateRange | undefined, next: DateRange): DateRange {
  const gte = [base?.gte, next.gte].filter((n): n is number => n != null);
  const lt = [base?.lt, next.lt].filter((n): n is number => n != null);
  const merged: DateRange = {};
  if (gte.length) merged.gte = Math.max(...gte);
  if (lt.length) merged.lt = Math.min(...lt);
  return merged;
}

/**
 * Resolve a (possibly partial) date value to its half-open granule range [start, end).
 * Accepts `YYYY`, `YYYY-MM`, `YYYY-MM-DD`. Returns null when unparseable.
 */
export function granuleRange(value: string): { start: number; end: number } | null {
  const m = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(value);
  if (!m) return null;
  const year = Number(m[1]);
  if (m[3] != null) {
    // Full day — validate the calendar date (rejects 2026-13-40 etc.).
    if (!isIsoDate(value)) return null;
    const start = Date.UTC(year, Number(m[2]) - 1, Number(m[3]));
    return { start, end: start + 86_400_000 };
  }
  if (m[2] != null) {
    const month = Number(m[2]);
    if (month < 1 || month > 12) return null;
    return { start: Date.UTC(year, month - 1, 1), end: Date.UTC(year, month, 1) };
  }
  return { start: Date.UTC(year, 0, 1), end: Date.UTC(year + 1, 0, 1) };
}

/** Operator → date range. `before` = < start; `after` = >= end (strictly past the granule); `on` = within. */
function dateOp(op: 'before' | 'after' | 'on', value: string): DateRange | null {
  const g = granuleRange(value);
  if (!g) return null;
  if (op === 'before') return { lt: g.start };
  if (op === 'after') return { gte: g.end };
  return { gte: g.start, lt: g.end };
}

function pushUnique<T>(arr: T[], v: T): void {
  if (!arr.includes(v)) arr.push(v);
}

/**
 * Parse a raw query string into a `ParsedQuery`. `opts.now` (default `Date.now()`) resolves the
 * relative specials `@today` / `@last7days`, keeping the function deterministic under test.
 */
export function parseQuery(raw: string, opts: { now?: number } = {}): ParsedQuery {
  const now = opts.now ?? Date.now();
  const filters = emptyFilters();
  const tokens: TextToken[] = [];
  const warnings: string[] = [];
  const textParts: string[] = [];

  for (const tokRaw of lex(raw)) {
    let tok = tokRaw;
    // Leading '-' negates the atom/operator that follows.
    let negated = false;
    if (tok.length > 1 && tok.startsWith('-')) {
      negated = true;
      tok = tok.slice(1);
    }

    // Bare boolean OR / grouping (only when not quoted).
    if (!negated && tok === 'OR') {
      tokens.push({ kind: 'or' });
      continue;
    }

    // Bear `@` specials.
    if (tok.startsWith('@') && tok.length > 1) {
      const s = tok.slice(1).toLowerCase();
      if (s === 'starred' || s === 'favorite' || s === 'favourite') {
        filters.starred = !negated;
      } else if (s === 'today') {
        const start = Math.floor(now / 86_400_000) * 86_400_000;
        filters.updated = intersect(filters.updated, { gte: start, lt: start + 86_400_000 });
      } else if (s === 'last7days' || s === 'lastweek') {
        filters.updated = intersect(filters.updated, { gte: now - 7 * 86_400_000 });
      } else if ((SPECIALS as readonly string[]).includes(s)) {
        if (!negated) pushUnique(filters.specials, s as Special);
      } else {
        warnings.push(`Unknown @special "${tok}" — searched as text`);
        textParts.push(tok);
        tokens.push({ kind: 'term', value: tok, negated, prefix: false, field: null });
      }
      continue;
    }

    // key:value operators.
    const colon = tok.indexOf(':');
    if (colon > 0) {
      const key = tok.slice(0, colon).toLowerCase();
      const val = tok.slice(colon + 1);
      if (handleOperator(key, val, negated, filters, warnings)) continue;
      // title: is a field scope, not an exact filter — handled as a scoped text atom below.
      if (key === 'title' && val) {
        addTextAtom(val, negated, 'title', tokens, textParts);
        continue;
      }
      // Unrecognized key:value — treat the whole token as literal text.
      warnings.push(`Unknown operator "${key}:" — searched as text`);
    }

    // Grouping parens may hug a term, e.g. `(cat` / `dog)`. Peel them into structure tokens.
    // (Quoted phrases are left intact — parens inside quotes are literal.)
    if (!tok.startsWith('"')) {
      let close = 0;
      while (tok.startsWith('(')) {
        tokens.push({ kind: 'group-open' });
        tok = tok.slice(1);
      }
      while (tok.endsWith(')')) {
        close++;
        tok = tok.slice(0, -1);
      }
      if (tok) addTextAtom(tok, negated, null, tokens, textParts);
      for (let i = 0; i < close; i++) tokens.push({ kind: 'group-close' });
      continue;
    }

    addTextAtom(tok, negated, null, tokens, textParts);
  }

  return { tokens, filters, text: textParts.join(' ').trim(), warnings };
}

/** Handle an exact-filter operator. Returns true if consumed; false if the caller should treat it as text. */
function handleOperator(
  key: string,
  val: string,
  negated: boolean,
  filters: QueryFilters,
  warnings: string[],
): boolean {
  switch (key) {
    case 'tag': {
      const t = val.replace(/^#/, '').toLowerCase();
      if (!t) return false;
      pushUnique(negated ? filters.notTags : filters.tags, t);
      return true;
    }
    case 'is': {
      const t = val.toLowerCase();
      if (t !== 'person' && t !== 'company' && t !== 'deal' && t !== 'topic') {
        warnings.push(`Unknown type "is:${val}" — ignored`);
        return true;
      }
      pushUnique(negated ? filters.notEntityTypes : filters.entityTypes, t);
      return true;
    }
    case 'in': {
      const scope = IN_ALIASES[val.toLowerCase()];
      if (!scope) {
        warnings.push(`Unknown scope "in:${val}" — ignored`);
        return true;
      }
      if (scope.corpus) pushUnique(negated ? filters.notCorpora : filters.corpora, scope.corpus);
      if (scope.pageType) pushUnique(negated ? filters.notPageTypes : filters.pageTypes, scope.pageType);
      return true;
    }
    case 'link': {
      const target = val.replace(/^\[\[/, '').replace(/\]\]$/, '').trim();
      if (target) pushUnique(filters.links, target);
      return true;
    }
    case 'before':
    case 'after':
    case 'on': {
      const range = dateOp(key, val);
      if (!range) {
        warnings.push(`Unparseable date "${key}:${val}" — ignored`);
        return true;
      }
      filters.updated = intersect(filters.updated, range);
      return true;
    }
    case 'created':
    case 'created-before':
    case 'created-after': {
      const op = key === 'created' ? 'on' : key === 'created-before' ? 'before' : 'after';
      const range = dateOp(op, val);
      if (!range) {
        warnings.push(`Unparseable date "${key}:${val}" — ignored`);
        return true;
      }
      filters.created = intersect(filters.created, range);
      return true;
    }
    default:
      return false;
  }
}

/** Add a free-text term or phrase token, tracking trailing `*` prefix and quoted phrases. */
function addTextAtom(
  raw: string,
  negated: boolean,
  field: FieldScope | null,
  tokens: TextToken[],
  textParts: string[],
): void {
  // Quoted phrase (possibly unterminated at end of input).
  if (raw.startsWith('"')) {
    const value = raw.replace(/^"/, '').replace(/"$/, '').trim();
    if (!value) return;
    tokens.push({ kind: 'phrase', value, negated, field });
    if (!negated) textParts.push(value);
    return;
  }
  let value = raw;
  let prefix = false;
  if (value.endsWith('*')) {
    prefix = true;
    value = value.slice(0, -1);
  }
  if (!value) return;
  tokens.push({ kind: 'term', value, negated, prefix, field });
  if (!negated) textParts.push(value);
}
